import {isPlatformBrowser} from '@angular/common';
import {inject, PLATFORM_ID} from '@angular/core';
// eslint-disable-next-line bnt/no-angular-router
import {Router} from '@angular/router';
import {
  APP_SETTING_STORAGE_KEY,
  DEFAULT_APP_SETTINGS,
  IAppSettings,
} from '@entities/app-settings';
import {ERouterPages} from '@entities/router/router.enums';
import {WA_WINDOW} from '@ng-web-apis/common';
import {Actions, ofType} from '@ngrx/effects';
import {Action, Store} from '@ngrx/store';
import {AnalyticsHelper} from '@shared/helper/analytics.helper';
import {StorageService} from '@shared/services/storage.service';
import {
  createFunctionalEffect,
  createFunctionalSideEffect,
} from '@shared/utils/effects.helper';
import {AuthenticationActions} from '@state/authentication/authentication.actions';
import {AuthenticationApi} from '@state/authentication/authentication.api';
import {authenticationFeature} from '@state/authentication/authentication.feature';
import {CommonActions} from '@state/common/common.actions';
import {commonFeature} from '@state/common/common.feature';
import {RouterActions} from '@state/router';
import {mapOdApiActions} from '@utils/map-od-api-actions.function';
import {
  concatMap,
  debounceTime,
  map,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs';

const SAVE_SETTINGS_DEBOUNCE_MS = 100;

export namespace CommonEffects {
  export const appInit$ = createFunctionalEffect(
    (
      actions$ = inject(Actions),
      storage = inject(StorageService),
      router = inject(Router),
      window = inject<Window>(WA_WINDOW),
      platformId = inject(PLATFORM_ID),
      store = inject(Store)
    ) =>
      actions$.pipe(
        ofType(CommonActions.appInit),
        take(1),
        tap(() => {
          if (isPlatformBrowser(platformId)) {
            AnalyticsHelper.init(router, window);
          }
        }),
        map(() => ({
          ...DEFAULT_APP_SETTINGS,
          ...storage.getItem<IAppSettings>(APP_SETTING_STORAGE_KEY),
        })),
        withLatestFrom(
          store.select(authenticationFeature.selectIsAuthenticated)
        ),
        concatMap(([storedSettings, isAuthenticated]) => {
          const resultActions: Action[] = [
            CommonActions.updateAppSettings({settings: storedSettings}),
          ];

          if (isAuthenticated) {
            resultActions.push(CommonActions.getUserInfo.requested());
          }

          return resultActions;
        })
      )
  );

  export const loadUserInfoOnLogin$ = createFunctionalEffect(
    (actions$ = inject(Actions)) =>
      actions$.pipe(
        ofType(AuthenticationActions.loginConfirm.succeeded),
        map(() => CommonActions.getUserInfo.requested())
      )
  );

  export const getUserInfo$ = createFunctionalEffect(
    (actions$ = inject(Actions), api = inject(AuthenticationApi)) =>
      actions$.pipe(
        ofType(CommonActions.getUserInfo.requested),
        switchMap(() =>
          api.getUser().pipe(mapOdApiActions(CommonActions.getUserInfo))
        )
      )
  );

  export const saveAppSettings$ = createFunctionalSideEffect(
    (
      actions$ = inject(Actions),
      storage = inject(StorageService),
      store = inject(Store)
    ) =>
      actions$.pipe(
        ofType(CommonActions.saveAppSettings),
        debounceTime(SAVE_SETTINGS_DEBOUNCE_MS),
        withLatestFrom(store.select(commonFeature.selectAppSettings)),
        tap(([, appSettings]) =>
          storage.setItem(APP_SETTING_STORAGE_KEY, appSettings)
        )
      )
  );

  export const saveAppSettingsActions$ = createFunctionalEffect(
    (actions$ = inject(Actions)) =>
      actions$.pipe(
        ofType(CommonActions.updateAppSettings),
        debounceTime(SAVE_SETTINGS_DEBOUNCE_MS),
        map(() => CommonActions.saveAppSettings())
      )
  );

  export const persistSearchHistory$ = createFunctionalEffect(
    (actions$ = inject(Actions)) =>
      actions$.pipe(
        ofType(CommonActions.searchSubmitted),
        map(() => CommonActions.saveAppSettings())
      )
  );

  export const openSearchPage$ = createFunctionalEffect(
    (actions$ = inject(Actions)) =>
      actions$.pipe(
        ofType(CommonActions.searchSubmitted),
        map(({search}) =>
          RouterActions.go({path: [ERouterPages.Search], queryParams: search})
        )
      )
  );
}
